<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import ChatList from 'dashboard/components/ChatList.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import LeadDistributionsAPI from 'dashboard/api/atendeTotal/leadDistributions';
import LostLeadDistributionMessage from './LostLeadDistributionMessage.vue';

export default {
  components: {
    ChatList,
    LostLeadDistributionMessage,
  },
  props: {
    leadDistributionId: {
      type: [String, Number],
      default: 0,
    },
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings } = useUISettings();

    return {
      uiSettings,
    };
  },
  data() {
    return {
      leadDistribution: {},
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
    }),
    showConversationList() {
      return !this.isOnExpandedLayout;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    hasRequiredLeadDistributionData() {
      return (
        this.leadDistribution.created_at &&
        this.leadDistribution.team?.first_interaction_deadline_minutes
      );
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });

    this.fetchLeadDistribution();
  },

  methods: {
    fetchLeadDistribution() {
      LeadDistributionsAPI.show(this.leadDistributionId)
        .then(({ data }) => {
          const {
            current_user_has_access,
            conversation: { display_id: conversationDisplayId },
          } = data;

          if (
            !current_user_has_access &&
            this.currentUser.role !== 'administrator'
          ) {
            this.leadDistribution = data;
            return;
          }

          this.$router.push({
            name: 'inbox_conversation',
            params: { conversation_id: conversationDisplayId },
          });
        })
        .catch(error => {
          const {
            status,
            data: { error: errorType },
          } = error.response;

          const isUnauthorized =
            status === 401 &&
            errorType === 'You are not authorized to do this action';

          if (isUnauthorized) {
            this.$router.push({ name: 'home' });
            return;
          }

          // eslint-disable-next-line no-console
          console.error('Error fetching lead distribution data:', error);
          this.$router.push({ name: 'home' });
        });
    },
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            this.$emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
  },
};
</script>

<template>
  <section class="flex w-full h-full bg-white dark:bg-slate-900">
    <ChatList
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :is-on-expanded-layout="isOnExpandedLayout"
      @conversation-load="onConversationLoad"
    />
    <LostLeadDistributionMessage
      v-if="hasRequiredLeadDistributionData"
      :is-on-expanded-layout="isOnExpandedLayout"
      :lead-distribution="leadDistribution"
    />
  </section>
</template>
